import { type Prettify } from '@canvas/type-utils';
import { type Vector2, type Region } from '@canvas/vector-types';

import { type Board } from './board.ts';
import { COLLAB_TOOL_ID_PREFIX } from './collaboration-tools/collaboration-tool.ts';
import { type EntityId } from './ecs.ts';

export type ElementType = string & { __elementType: symbol };
export const elementType = (e: string) => e as ElementType;

/**
 * A reference to another element on the Canvas board.
 */
export type ElementId = string;

/**
 * @description NOTE: We use 1-based indexing of values to avoid the pitfalls of
 * handling '0' in JavaScript, which is a falsy value by default.
 */
export enum ElementSource {
  /**
   * The element was created by a human user.
   */
  USER = 1,
  /**
   * The element was created by an AI agent, on behalf of a user.
   */
  AGENT = 2,
}

/**
 * Note: Consider using {@link FullElementData} and/or {@link AnyElementData} as this type definition will
 * only provide you with fields that are shared across all element data.
 */
export interface ElementData<Type extends ElementType = any> {
  type: Type;
  id: string;
  position: Vector2;
  size: Vector2;
  lockedByUser?: string;
  /**
   * The source from which the element was created. It is possible for this to be an
   * AI agent, or a human user. Used for the purposes of post-create analysis.
   */
  source?: ElementSource;
  /**
   * The optional layer that this element belongs to.
   * For whiteboards, this will eventually be the id of a y-layer
   * For slides, this is the id of a slide
   */
  layerId?: string;
}

export type UnknownElementData = Prettify<ElementData<any>>;

export const isElementData = (data: any): data is ElementData => {
  return (
    typeof data === 'object' &&
    data.id !== undefined &&
    data.type !== undefined &&
    !data.id.startsWith(COLLAB_TOOL_ID_PREFIX)
  );
};

export const isElementWithText = (
  elementData: ElementData<any>,
): elementData is ElementDataWithContent => {
  return !!elementData && typeof elementData === 'object' && 'text' in elementData;
};

export const isElementWithTitle = (
  elementData: ElementData<any>,
): elementData is Element & { title: string } => {
  return !!elementData && typeof elementData === 'object' && 'title' in elementData;
};
/**
 * Any element data that has text content should use this interface. We use
 * interface to know which elements may have text content in templates that need
 * to be translated.
 */
export interface ElementDataWithContent<
  T extends ElementType = ElementType,
> extends ElementData<T> {
  text: string;
}

/**
 * Consider using {@link YAllElements} in cases where your typing a variable that
 * could be any YElement class instance.
 *
 * This interface is designed to be implemented by classes that need to be used
 * as a y element.
 *
 * Using this as, for instance, a parameter type instead of {@link YAllElements}
 * will force you to perform type casts in order to retrieve fields specific to
 * individual elements (even after check the element's type).
 */
export interface Element<Type extends ElementType = any> extends ElementData<Type> {
  intersects(point: Vector2): boolean;
  toJSON(): ElementData<Type>;

  /**
   * Moves the zIndex of the element so that it is at the back.
   * @returns The new zIndex of the element.
   */
  moveZIndexToBack(): number;

  /**
   * Moves the zIndex of the element so that it is at the front.
   * @returns The new zIndex of the element.
   */
  moveZIndexToFront(): number;

  /**
   * Moves the zIndex of the element so that it is just before the given elementId.
   * @param beforeId The ID of the element to move this element before.
   * @returns The new zIndex of the element.
   */
  moveZIndexBefore(beforeId: string): number;

  /**
   * Moves the zIndex of the element so that it is just after the given elementId.
   * @param afterId The ID of the element to move this element after.
   * @returns The new zIndex of the element.
   */
  moveZIndexAfter(afterId: string): number;

  get zIndex(): number;
  readonly region: Region;
}

export type ElementWithBasisSizing = Element & {
  basisSize: Vector2;
};

export type ElementWithBasisPositioning = Element & {
  basisPosition: Vector2;
};

export type PositioningFlags = {
  basisSizing: boolean;
  basisPositioning: boolean;
  fontScaling: boolean;
  /**
   * Repurposes the original size and position fields for basis sizing.
   *
   * You'll want to turn this on for any new element
   */
  reuseLegacySizingField?: boolean;
};

export type ElementFlags = {
  positioning: PositioningFlags;
};

export type ElementFlagsMap = Record<string, ElementFlags>;

export type UnknownElement = Element<any>;

export const unknownElementType = elementType('unknown');

export type ElementDefinition<T extends Element = Element> = {
  constructor: new (...args: any[]) => T;
  fromJSON: (data: ElementData, ...args: any[]) => T;
};

export type ElementMap<T extends Element = Element> = Record<
  string,
  ElementDefinition<T>
>;

export const getEntityElementType = (entityId: EntityId | undefined, board: Board) => {
  return entityId ? (board.elements[entityId]?.type ?? 'unknown') : 'unknown';
};
